import { ButtonDropdown, IconButton, NavItemSeparator, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BlockCancelInvoiceActionVariant } from '@features/invoices/elements/BlockCancelInvoiceActionModal/enums/blockCancelInvoiceActionVariant'
import { getBlockCancelInvoiceActionModalId } from '@features/invoices/elements/BlockCancelInvoiceActionModal/utils/getBlockCancelInvoiceActionModalId'
import { useInvoiceStatusActions } from '@features/invoices/hooks/useInvoiceStatusActions'
import { getCancelDepositInvoiceEligibility } from '@features/invoices/utils/depositInvoices/getCancelDepositInvoiceEligibility'

import { ModalId } from '../../../../../../../../enums/modalId'
import { useInvoice } from '../../../../contexts/invoiceContext'
import { useTrackInvoiceView } from '../../../../hooks/useTrackInvoiceView'
import { InvoiceActionType } from '../../enums/InvoiceActionType'
import { useCreateCreditNote } from '../../hooks/useCreateCreditNote'
import { useDuplicateInvoice } from '../../hooks/useDuplicateInvoice'
import { useInvoiceViewActions } from '../../hooks/useInvoiceViewActions'
import { getInvoiceActionsMenuItems } from './utils/getInvoiceActionsMenuItems'

export const InvoiceActionsMenu = (): ReactElement | null => {
  const { t } = useTranslation()
  const { open: openCancelInvoiceModal } = useModal(ModalId.InvoiceCancellationModal)
  const { open: openSendPaymentReminderModal } = useModal(ModalId.SendPaymentReminderModal)
  const { trackInvoiceView } = useTrackInvoiceView()
  const { invoiceActionsDisplayedAsMenuItems } = useInvoiceViewActions()
  const { duplicateInvoice } = useDuplicateInvoice()
  const { createCreditNote } = useCreateCreditNote()
  const { documentType, invoice, invoiceId } = useInvoice()
  const { open: openBlockCancelInvoiceActionModal } = useModal(
    getBlockCancelInvoiceActionModalId(invoiceId, BlockCancelInvoiceActionVariant.CreditNote),
  )
  const { markInvoiceAsSent, unmarkInvoiceAsSent } = useInvoiceStatusActions({ documentType, invoiceId })

  const { isCancelBlockedByFinalInvoice } = getCancelDepositInvoiceEligibility(invoice)

  const invoiceActionsMenuItems = useMemo(
    () => getInvoiceActionsMenuItems(invoiceActionsDisplayedAsMenuItems, t),
    [invoiceActionsDisplayedAsMenuItems, t],
  )

  const handleActionSelect = useCallback(
    (_id: string, item: InvoiceActionType | NavItemSeparator) => {
      switch (item) {
        case InvoiceActionType.Duplicate:
          trackInvoiceView('Duplicate action clicked')
          duplicateInvoice()
          break
        case InvoiceActionType.CreateCreditNote:
          trackInvoiceView('Create credit note action clicked')
          if (isCancelBlockedByFinalInvoice) {
            openBlockCancelInvoiceActionModal()
            break
          }
          createCreditNote()
          break
        case InvoiceActionType.Cancel:
          trackInvoiceView('Cancel Invoice clicked')
          openCancelInvoiceModal()
          break
        case InvoiceActionType.SendPaymentReminder:
          trackInvoiceView('Send payment reminder clicked')
          openSendPaymentReminderModal()
          break
        case InvoiceActionType.MarkAsSent:
          trackInvoiceView('Mark as sent action clicked')
          markInvoiceAsSent()
          break
        case InvoiceActionType.UnmarkAsSent:
          trackInvoiceView('Unmark as sent action clicked')
          unmarkInvoiceAsSent()
          break
        default:
          break
      }
    },
    [
      createCreditNote,
      duplicateInvoice,
      isCancelBlockedByFinalInvoice,
      markInvoiceAsSent,
      unmarkInvoiceAsSent,
      openBlockCancelInvoiceActionModal,
      openCancelInvoiceModal,
      openSendPaymentReminderModal,
      trackInvoiceView,
    ],
  )

  if (invoiceActionsMenuItems.length === 0) {
    return null
  }

  return (
    <ButtonDropdown
      items={invoiceActionsMenuItems}
      onSelect={handleActionSelect}
      placement="bottom-end"
      triggerElement={<IconButton icon="ellipsis" variant="outlined" />}
    />
  )
}
